import { createClient } from '@/lib/supabase/client';

// Chart screenshots attached to comments: downscale + re-encode client-side so a
// 2-6MB platform screenshot lands around 100-250KB, then upload through
// /api/trades/upload-image (auth + "is it really a chart" AI moderation live there).
const MAX_WIDTH = 1280; // keeps SL/TP labels readable, keeps files small
const JPEG_QUALITY = 0.8;

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
    const { data: { session: refreshed } } = await supabase.auth.refreshSession();
    if (refreshed?.access_token) return { Authorization: `Bearer ${refreshed.access_token}` };
  } catch {}
  return {};
}

/** Downscale to ≤1280px wide and re-encode as JPEG (q0.8). */
export async function compressChartImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_WIDTH / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d')!;
  // JPEG has no alpha — a transparent PNG would otherwise turn black in odd spots;
  // fill with the app's chart background so it blends into the dark UI.
  ctx.fillStyle = '#0d1117';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not process the image.'))),
      'image/jpeg',
      JPEG_QUALITY,
    ),
  );
}

/**
 * Compress and upload a chart screenshot for a comment.
 * Returns the public URL to store on the comment. Throws with a
 * user-presentable message when moderation or upload rejects it.
 */
export async function uploadCommentImage(file: File): Promise<string> {
  const blob = await compressChartImage(file);
  const form = new FormData();
  form.append('file', new File([blob], 'chart.jpg', { type: 'image/jpeg' }));
  const headers = await getAuthHeaders();
  const res = await fetch('/api/trades/upload-image', {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  });
  const body = await res.json().catch(() => ({} as { error?: string; url?: string }));
  if (!res.ok || !body.url) throw new Error(body.error || `Upload failed (${res.status})`);
  return body.url as string;
}
